'use client';

import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  startSelfOnboard,
  validateSelfOnboardToken,
  SelfOnboardApiError,
} from '@/services/self-onboard.service';
import { writeSelfOnboardSession } from '@/lib/public-routing';

export default function SelfOnboardDetailsPage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');
  const [personalPhone, setPersonalPhone] = useState('');
  const [personalEmail, setPersonalEmail] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [locked, setLocked] = useState<{ role?: string; dept?: string; designation?: string }>({});
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void validateSelfOnboardToken(token).then((result) => {
      if (cancelled || !result.valid) return;
      setFirstName(result.editableHints?.firstName ?? '');
      setLastName(result.editableHints?.lastName ?? '');
      setMaskedEmail(result.maskedEmail ?? '');
      setLocked({
        role: result.lockedFields?.orgRoleName,
        dept: result.lockedFields?.departmentName,
        designation: result.lockedFields?.designation,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim() || !dateOfBirth) {
      setError('First name, last name, and date of birth are required.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const dobIso = new Date(`${dateOfBirth}T00:00:00.000Z`).toISOString();
      const result = await startSelfOnboard(token, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        dateOfBirth: dobIso,
        gender: gender || undefined,
        personalPhone: personalPhone.trim() || undefined,
        personalEmail: personalEmail.trim() || undefined,
      });
      writeSelfOnboardSession({
        sessionId: result.sessionId,
        token,
        maskedEmail: result.maskedEmail,
      });
      navigate(`/self-onboard/${token}/verify-email`);
    } catch (err) {
      setError(err instanceof SelfOnboardApiError ? err.message : 'Failed to save details.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl p-8">
      <h1 className="text-xl font-bold text-slate-900 dark:text-white mb-1">Your details</h1>
      <p className="text-sm text-slate-500 mb-6">
        Role and department are set by your HR team and cannot be changed here.
      </p>

      <div className="flex flex-wrap gap-2 mb-6">
        {[maskedEmail, locked.role, locked.dept, locked.designation]
          .filter(Boolean)
          .map((chip) => (
            <span
              key={chip}
              className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-3 py-1 text-xs text-slate-600 dark:text-slate-300"
            >
              <i className="ri-lock-2-line" /> {chip}
            </span>
          ))}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm">
            <span className="text-slate-600 dark:text-slate-300">First name *</span>
            <input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2.5"
              required
            />
          </label>
          <label className="block text-sm">
            <span className="text-slate-600 dark:text-slate-300">Last name *</span>
            <input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2.5"
              required
            />
          </label>
        </div>

        <label className="block text-sm">
          <span className="text-slate-600 dark:text-slate-300">Date of birth *</span>
          <input
            type="date"
            value={dateOfBirth}
            onChange={(e) => setDateOfBirth(e.target.value)}
            className="mt-1 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2.5"
            required
          />
        </label>

        <label className="block text-sm">
          <span className="text-slate-600 dark:text-slate-300">Gender</span>
          <select
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            className="mt-1 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2.5"
          >
            <option value="">Prefer not to say</option>
            <option value="female">Female</option>
            <option value="male">Male</option>
            <option value="other">Other</option>
          </select>
        </label>

        <label className="block text-sm">
          <span className="text-slate-600 dark:text-slate-300">Personal phone</span>
          <input
            value={personalPhone}
            onChange={(e) => setPersonalPhone(e.target.value)}
            className="mt-1 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2.5"
          />
        </label>

        <label className="block text-sm">
          <span className="text-slate-600 dark:text-slate-300">Personal email (optional)</span>
          <input
            type="email"
            value={personalEmail}
            onChange={(e) => setPersonalEmail(e.target.value)}
            className="mt-1 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2.5"
          />
        </label>

        {error ? <p className="text-sm text-red-600">{error}</p> : null}

        <button
          type="submit"
          disabled={submitting}
          className="w-full py-3 rounded-xl bg-gradient-to-r from-[var(--hrm-primary)] to-[var(--hrm-primary-dark)] text-white font-semibold disabled:opacity-60"
        >
          {submitting ? 'Sending code…' : 'Continue to verification'}
        </button>
      </form>

      <Link
        to={`/self-onboard/${token}`}
        className="mt-4 inline-block text-sm text-slate-500 hover:text-slate-700"
      >
        ← Back
      </Link>
    </div>
  );
}
